// Immeubles, lots et documents communs (F-007).
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { post, put, upload } from "@core/api";
import { date, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Button, Card, Checkbox, Empty, ErrorBox, FileDrop, FileLine, Input, KeyValues, Loading, Modal, Notice, PageHeader, Select, Table, Textarea, fieldError, useToast } from "@core/ui";
import { AddressFields, EMPTY_ADDRESS, OccupationBadge, intOrNull, str, type Address, type Property } from "./shared";

type Building = {
  id: string;
  name: string;
  address: Address;
  address_line: string;
  in_copropriete: boolean;
  details: Record<string, any>;
  archived_at: string | null;
  lots?: number;
  housing_lots?: number;
  leased_lots?: number;
};

type BuildingDetail = Omit<Building, "lots"> & {
  lots: (Property & { tantiemes: number | null; tantiemes_share: string | null })[];
  tantiemes: { declared_total: number | null; managed_total: number; reference_total: number; example_1000_eur: { property_id: string; label: string; cents: number }[]; note: string };
  documents: { id: string; title: string; filename: string; size_bytes: number; url: string; created_at: string }[];
  can_edit: boolean;
};

export function BuildingsPage() {
  const org = useOrg();
  const nav = useNavigate();
  const q = useGet<{ items: Building[] }>(org.api("/buildings"));
  const [creating, setCreating] = useState(false);
  const items = q.data?.items ?? [];
  return (
    <div className="page">
      <PageHeader
        eyebrow="Immeubles"
        title="Immeubles"
        intro="Regroupez les lots d’un même immeuble : logements, chambres, caves et parkings, avec leurs tantièmes et les documents communs."
        actions={org.can("operate") ? <Button onClick={() => setCreating(true)}>Ajouter un immeuble</Button> : null}
      />
      <ErrorBox error={q.error} />
      {q.isLoading ? <Loading /> : null}
      {!q.isLoading && items.length === 0 ? (
        <Empty title="Aucun immeuble." action={org.can("operate") ? <Button variant="secondaire" onClick={() => setCreating(true)}>Ajouter un immeuble</Button> : undefined}>
          Un immeuble est utile si vous y possédez plusieurs lots ou si vous voulez y rattacher des documents de copropriété.
        </Empty>
      ) : null}
      {items.length ? (
        <Table caption="Immeubles">
          <thead>
            <tr>
              <th>Immeuble</th>
              <th>Adresse</th>
              <th>Lots gérés</th>
              <th>Loués</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id}>
                <td>
                  <Link to={org.to(`immeubles/${b.id}`)}>{b.name}</Link>
                  <span className="pat-sub">
                    {b.in_copropriete ? "Copropriété" : "Monopropriété"}
                    {b.archived_at ? " · archivé" : ""}
                  </span>
                </td>
                <td className="small">{b.address_line}</td>
                <td className="num">
                  {b.lots ?? 0}
                  <span className="pat-sub">dont {b.housing_lots ?? 0} logement(s)</span>
                </td>
                <td className="num">{b.leased_lots ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      <Modal open={creating} onClose={() => setCreating(false)} title="Ajouter un immeuble">
        {creating ? (
          <BuildingForm
            onDone={(b) => {
              setCreating(false);
              nav(org.to(`immeubles/${b.id}`));
            }}
            onCancel={() => setCreating(false)}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function BuildingForm({ building, onDone, onCancel }: { building?: Pick<Building, "id" | "name" | "address" | "in_copropriete" | "details">; onDone: (b: Building) => void; onCancel: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const d = building?.details ?? {};
  const [f, setF] = useState({
    name: building?.name ?? "",
    address: building?.address ?? EMPTY_ADDRESS,
    in_copropriete: building?.in_copropriete ?? true,
    syndic_name: str(d.syndic_name),
    syndic_contact: str(d.syndic_contact),
    construction_year: str(d.construction_year),
    tantiemes_total: str(d.tantiemes_total),
    lots_total: str(d.lots_total),
    notes: str(d.notes),
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const err = (k: string) => fieldError(error, k);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = { ...f, construction_year: intOrNull(f.construction_year), tantiemes_total: intOrNull(f.tantiemes_total), lots_total: intOrNull(f.lots_total) };
    try {
      const saved = building ? await put<Building>(org.api(`/buildings/${building.id}`), body) : await post<Building>(org.api("/buildings"), body);
      invalidate(org.api("/buildings"), org.api("/properties"));
      toast(building ? "Immeuble mis à jour." : "Immeuble ajouté.");
      onDone(saved);
    } catch (e2) {
      setError(e2);
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="stack" onSubmit={submit} noValidate>
      <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
      <Input label="Nom de l’immeuble" required value={f.name} error={err("name")} placeholder="Ex. 12 rue des Tanneurs" onChange={(e) => setF({ ...f, name: e.target.value })} />
      <AddressFields value={f.address} onChange={(a) => setF({ ...f, address: a })} required errors={{ line1: err("address.line1"), postal_code: err("address.postal_code"), city: err("address.city") }} />
      <Checkbox label="Immeuble en copropriété" checked={f.in_copropriete} onChange={(v) => setF({ ...f, in_copropriete: v })} />
      <div className="form-row">
        <Input label="Syndic" value={f.syndic_name} onChange={(e) => setF({ ...f, syndic_name: e.target.value })} />
        <Input label="Contact du syndic" value={f.syndic_contact} onChange={(e) => setF({ ...f, syndic_contact: e.target.value })} />
      </div>
      <div className="form-row">
        <Input label="Année de construction" inputMode="numeric" value={f.construction_year} error={err("construction_year")} onChange={(e) => setF({ ...f, construction_year: e.target.value })} />
        <Input label="Total des tantièmes" inputMode="numeric" help="Selon le règlement de copropriété." value={f.tantiemes_total} error={err("tantiemes_total")} onChange={(e) => setF({ ...f, tantiemes_total: e.target.value })} />
        <Input label="Nombre total de lots" inputMode="numeric" value={f.lots_total} error={err("lots_total")} onChange={(e) => setF({ ...f, lots_total: e.target.value })} />
      </div>
      <Textarea label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      <div className="form-actions">
        <Button variant="discret" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" busy={busy}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

const DOC_KINDS = [
  { value: "reglement_copropriete", label: "Règlement de copropriété" },
  { value: "proces_verbal_ag", label: "Procès-verbal d’assemblée générale" },
  { value: "appel_de_charges", label: "Appel de charges du syndic" },
  { value: "diagnostic_parties_communes", label: "Diagnostic des parties communes" },
  { value: "autre", label: "Autre document commun" },
];

export function BuildingPage() {
  const org = useOrg();
  const { buildingId = "" } = useParams();
  const toast = useToast();
  const invalidate = useInvalidate();
  const q = useGet<BuildingDetail>(org.api(`/buildings/${buildingId}`));
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState("reglement_copropriete");
  const [busy, setBusy] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const b = q.data;
  const canEdit = b.can_edit && !b.archived_at;
  return (
    <div className="page">
      <PageHeader
        eyebrow={b.in_copropriete ? "Immeuble en copropriété" : "Immeuble"}
        title={b.name}
        intro={b.address_line}
        actions={
          b.can_edit ? (
            b.archived_at ? (
              <Button
                variant="secondaire"
                onClick={async () => {
                  await post(org.api(`/buildings/${b.id}/restore`));
                  invalidate(org.api("/buildings"));
                }}
              >
                Restaurer
              </Button>
            ) : (
              <>
                <Button
                  variant="secondaire"
                  onClick={async () => {
                    try {
                      await post(org.api(`/buildings/${b.id}/archive`));
                      invalidate(org.api("/buildings"));
                      toast("Immeuble archivé.");
                    } catch (e: any) {
                      toast(e.message, "erreur");
                    }
                  }}
                >
                  Archiver
                </Button>
                <Button onClick={() => setEditing(true)}>Modifier</Button>
              </>
            )
          ) : null
        }
      />
      {b.archived_at ? <Notice tone="attention">Immeuble archivé le {date(b.archived_at)}.</Notice> : null}
      <div className="grid-2">
        <Card title="Informations">
          <KeyValues
            items={[
              ["Copropriété", b.in_copropriete ? "Oui" : "Non"],
              ["Syndic", b.details.syndic_name ?? "—"],
              ["Contact du syndic", b.details.syndic_contact ?? "—"],
              ["Année de construction", b.details.construction_year ?? "—"],
              ["Total des tantièmes", b.details.tantiemes_total ?? "—"],
              ["Nombre total de lots", b.details.lots_total ?? "—"],
            ]}
          />
          {b.details.notes ? <p className="small">{b.details.notes}</p> : null}
        </Card>
        <Card title="Clé de répartition">
          <p className="small muted">{b.tantiemes.note}</p>
          <KeyValues
            items={[
              ["Tantièmes des lots gérés", b.tantiemes.managed_total || "—"],
              ["Total de référence", b.tantiemes.reference_total || "—"],
            ]}
          />
          {b.tantiemes.example_1000_eur.length ? (
            <>
              <p className="small">Exemple : 1 000,00 € de charges communes réparties entre vos lots gérés.</p>
              <ul className="pat-due">
                {b.tantiemes.example_1000_eur.map((e) => (
                  <li key={e.property_id}>
                    <span>{e.label}</span>
                    <span className="num">{money(e.cents)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="small muted">Renseignez les tantièmes de chaque lot sur sa fiche pour afficher la répartition.</p>
          )}
        </Card>
      </div>
      <Card
        title="Lots"
        actions={
          canEdit ? (
            <Link className="bv-btn bv-btn-secondaire bv-btn-petit" to={org.to("logements")}>
              Ajouter un lot depuis « Logements »
            </Link>
          ) : null
        }
      >
        {b.lots.length === 0 ? (
          <p className="muted">Aucun lot rattaché. Choisissez cet immeuble dans la fiche d’un logement, d’une cave ou d’un parking.</p>
        ) : (
          <Table caption="Lots de l’immeuble">
            <thead>
              <tr>
                <th>Lot</th>
                <th>Surface</th>
                <th>Tantièmes</th>
                <th>Occupation</th>
              </tr>
            </thead>
            <tbody>
              {b.lots.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.parent_id ? <span aria-hidden="true">↳ </span> : null}
                    <Link to={org.to(`logements/${l.id}`)}>{l.label}</Link>
                    <span className="pat-sub">
                      {l.kind_label}
                      {l.details?.lot_number ? ` · lot ${l.details.lot_number}` : ""}
                      {l.archived_at ? " · archivé" : ""}
                    </span>
                  </td>
                  <td className="num">{l.surface_m2 !== null ? `${l.surface_m2.toLocaleString("fr-FR")} m²` : "—"}</td>
                  <td className="num">
                    {l.tantiemes ?? "—"}
                    {l.tantiemes_share ? <span className="pat-sub">{l.tantiemes_share}</span> : null}
                  </td>
                  <td>
                    <OccupationBadge occupation={l.occupation} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Card title="Documents communs">
        {b.documents.length === 0 ? <p className="muted">Aucun document commun.</p> : null}
        {b.documents.map((d) => (
          <FileLine key={d.id} name={d.title || d.filename} size={d.size_bytes} href={d.url} right={<span className="small muted">{date(d.created_at)}</span>} />
        ))}
        {canEdit ? (
          <div className="stack-sm">
            <Select label="Type de document" value={kind} options={DOC_KINDS} onChange={(e) => setKind(e.target.value)} />
            <FileDrop
              busy={busy}
              onFiles={async (files) => {
                if (!files[0]) return;
                setBusy(true);
                try {
                  const form = new FormData();
                  form.append("kind", kind);
                  form.append("file", files[0]);
                  await upload(org.api(`/buildings/${b.id}/documents`), form);
                  invalidate(org.api(`/buildings/${b.id}`));
                  toast("Document ajouté.");
                } catch (e: any) {
                  toast(e.message, "erreur");
                } finally {
                  setBusy(false);
                }
              }}
            />
          </div>
        ) : null}
      </Card>
      <Modal open={editing} onClose={() => setEditing(false)} title="Modifier l’immeuble">
        {editing ? <BuildingForm building={b} onDone={() => setEditing(false)} onCancel={() => setEditing(false)} /> : null}
      </Modal>
    </div>
  );
}
